/**
 * Tujuan: Project listing page — displays all portfolio projects in a 2-column case-study grid
 * Caller: App.js route /project
 * Dependensi: react-bootstrap, react-router-dom, react-icons, project image assets
 * Main Functions: Projects()
 * Side Effects: None
 * Changelog:
 *   2026-09 — Added thesis-absa card (NLP/ABSA Master's Thesis)
 */
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { AiFillGithub } from "react-icons/ai";
import { FaEye, FaGlobe, FaChartBar } from "react-icons/fa";
import { Link } from "react-router-dom";

/* Real project preview images */
import tiketyukImg   from "../assets/tiketyuk-logo-new.png";
import thesisImg     from "../assets/thesis.png";
import flowspaceImg  from "../assets/flowspace.png";
import energyImg     from "../assets/energy.png";
import gameImg       from "../assets/dashboard.png";
import warehouseImg  from "../assets/cust_segment_chart.png";

/**
 * projectList — single source of truth for the Projects page card grid.
 *
 * Fields:
 *   slug        → links to /project/:slug detail page (null = no detail page)
 *   image       → preview image imported above
 *   imageAlt    → alt text for the preview image
 *   category    → small uppercase label shown above title
 *   title       → card heading
 *   desc        → 1–2 sentence description
 *   tags        → short tech metadata shown below description
 *   website     → live demo / website URL (null if not applicable)
 *   github      → GitHub repo URL (null if not applicable)
 *   tableau     → Tableau public link (null if not applicable)
 *   isBiz       → true → purple accent variant
 *   hideDetail  → true → omit the "Details" link (no ProjectDetail page)
 */
const projectList = [
  {
    slug: "tiketyuk",
    image: tiketyukImg,
    imageAlt: "TiketYuk ordering website",
    category: "Real Business · Web Development",
    title: "TiketYuk",
    desc: "Built and operated a concert ticket concierge service handling 100+ orders with a 4-person team, supported by a custom Next.js ordering site with Discord and Google Sheets automation.",
    tags: ["Next.js", "Google Sheets API", "Discord Webhook", "Vercel"],
    website: "https://www.tiketyuk.me/",
    github: null,
    tableau: null,
    isBiz: true,
    hideDetail: false,
  },
  {
    slug: "thesis-absa",
    image: thesisImg,
    imageAlt: "Thesis ABSA KlikIndomaret",
    category: "NLP · Machine Learning · Master's Thesis",
    title: "Klasifikasi Multi-Aspect dalam ABSA pada Ulasan KlikIndomaret menggunakan Model Transformer",
    desc: "Penelitian tesis S2 membandingkan IndoBERT dan IndoRoBERTa untuk klasifikasi sentimen multi-aspek pada ulasan KlikIndomaret — mencakup aspek Aplikasi, Pembayaran, Harga, Pelayanan, dan Produk.",
    tags: ["NLP", "ABSA", "IndoBERT", "IndoRoBERTa", "Transformer", "Python"],
    website: null,
    github: null,
    tableau: null,
    isBiz: false,
    isThesis: true,
    hideDetail: false,
  },
  {
    slug: null,
    image: flowspaceImg,
    imageAlt: "FlowSpace productivity workspace dashboard",
    category: "Web Development · UI/UX",
    title: "FlowSpace",
    desc: "A productivity-focused web application that brings task management, daily focus tracking, goal setting, and workspace organisation into one calm interface.",
    tags: ["React", "Tailwind CSS", "Vercel", "Full-Stack"],
    website: "https://flowspace-blue.vercel.app/",
    github: null,
    tableau: null,
    isBiz: false,
    hideDetail: true,
  },
  {
    slug: "electricity-analysis",
    image: energyImg,
    imageAlt: "Electricity Energy Analysis Tableau Dashboard",
    category: "Data Analytics · Visualization",
    title: "Electricity Energy Analysis Dashboard",
    desc: "End-to-end pipeline from raw global electricity and CO₂ data to a Tableau dashboard, revealing fossil fuel dependency and renewable energy gaps across countries.",
    tags: ["Python", "PostgreSQL", "ETL", "Tableau"],
    website: null,
    github: "https://github.com/nabilahintaka/Electricity-Energy-Analyst",
    tableau: "https://public.tableau.com/views/ElectricalEnergyDashboard/Dashboard1",
    isBiz: false,
    hideDetail: false,
  },
  {
    slug: "video-game-sales",
    image: gameImg,
    imageAlt: "Video Game Sales Analysis Tableau Dashboard",
    category: "Data Analytics · Visualization",
    title: "Video Game Sales Analysis",
    desc: "Exploratory analysis of global video game sales across genre, platform, and region using Python and Tableau, surfacing publisher dominance and regional market patterns.",
    tags: ["Python", "Pandas", "Seaborn", "Tableau", "EDA"],
    website: null,
    github: "https://github.com/nabilahintaka/Video-Game-Sales-Analysis",
    tableau: "https://public.tableau.com/views/VideoGameSalesDashboard_17376436610220/Dashboard",
    isBiz: false,
    hideDetail: false,
  },
  {
    slug: "thelook-dwh",
    image: warehouseImg,
    imageAlt: "E-Commerce Data Warehouse customer segmentation chart",
    category: "Data Engineering · Analytics",
    title: "E-Commerce Data Warehouse Analysis",
    desc: "Star schema data warehouse built from raw TheLook e-commerce data, enabling scalable SQL analytics across products, customers, and time — with revenue and segmentation insights.",
    tags: ["Python", "SQL", "PostgreSQL", "Data Modeling"],
    website: null,
    github: "https://github.com/nabilahintaka/Data-Warehouse-TheLook",
    tableau: null,
    isBiz: false,
    hideDetail: false,
  },
];

function Projects() {
  return (
    <Container fluid className="project-section">
      <Container className="section-container">

        {/* Page heading */}
        <h1 className="project-title">
          My Recent <span>Works</span>
        </h1>
        <p className="project-subtitle">
          A selection of projects across web development, data analysis, and business operations.
        </p>

        {/* 2-column grid */}
        <Row className="g-4">
          {projectList.map((proj, idx) => (
            <Col md={6} key={idx}>
              <div className={`project-card${proj.isBiz ? " project-card-biz" : ""}`}>

                {/* Preview image */}
                <div className="project-img-wrapper">
                  <img src={proj.image} alt={proj.imageAlt} />
                </div>

                {/* Category label */}
                <span className="project-category">{proj.category}</span>

                {/* Title */}
                <h3>{proj.title}</h3>

                {/* Description */}
                <p>{proj.desc}</p>

                {/* Tech tags */}
                <div className="project-tech-tags">
                  {proj.tags.map((tag) => (
                    <span key={tag} className="project-tech-tag">{tag}</span>
                  ))}
                </div>

                {/* Action buttons — only show links that actually exist */}
                <div className="project-buttons">
                  {proj.github && (
                    <a href={proj.github} target="_blank" rel="noreferrer">
                      <button className="project-btn">
                        <AiFillGithub /> GitHub
                      </button>
                    </a>
                  )}
                  {proj.tableau && (
                    <a href={proj.tableau} target="_blank" rel="noreferrer">
                      <button className="project-btn project-btn-tableau">
                        <FaChartBar /> Tableau
                      </button>
                    </a>
                  )}
                  {proj.website && (
                    <a href={proj.website} target="_blank" rel="noreferrer">
                      <button className={`project-btn${proj.isBiz ? " project-btn-biz" : " project-btn-demo"}`}>
                        <FaGlobe /> {proj.isBiz ? "Website" : "Live Demo"}
                      </button>
                    </a>
                  )}
                  {!proj.hideDetail && proj.slug && (
                    <Link to={`/project/${proj.slug}`}>
                      <button className="project-btn outline">
                        <FaEye /> Details
                      </button>
                    </Link>
                  )}
                </div>

              </div>
            </Col>
          ))}
        </Row>

      </Container>
    </Container>
  );
}

export default Projects;
